import { Box } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { datasetApi } from '@/api/datasetApi'
import { getApiError } from '@/api/helpers/apiError'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useModelContext } from '@/providers/ModelContext'
import {
  useGetDatasetDetails,
  useGetDatasetVersionsList
} from '@/queries/datasetQueries'
import { useUpdateModel } from '@/queries/modelQueries'
import { showToast } from '@/theme/notifications'
import { Dataset, DatasetVersionStatus } from '@/types/dataset'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { CreateTrainingHandler } from '../trainings/CreateTrainingHandler'
import { TrainingDrawer } from '../trainings/TrainingDrawer/TrainingDrawer'
import { ConfirmDatasetCompletion } from './ConfirmDatasetCompletion/ConfirmDatasetCompletion'
import { DatasetToolbar } from './DatasetToolbar/DatasetToolbar'
import { DatasetToolbarSkeleton } from './DatasetToolbar/DatasetToolbarSkeleton'
import { UploadableMediaListHandler } from './UploadableMediaList/UploadableMediaListHandler'

export const LabelDataPage = () => {
  const intl = useIntl()

  const [completionConfirmedVersionId, setCompletionConfirmedVersionId] =
    useState('')

  const [
    isTrainingDrawerOpened,
    { open: openTrainingDrawer, close: closeTrainingDrawer }
  ] = useDisclosure(false)

  const [
    isConfirmCompletionModalOpened,
    { open: openConfirmCompletionModal, close: closeConfirmCompletionModal }
  ] = useDisclosure(false)

  const { application } = useApplicationContext()
  const { model } = useModelContext()

  const datasetId = model?.dataset_version?.dataset || ''
  const datasetVersionId = model?.dataset_version?.id || ''

  const { mutateAsync: updateModel, isPending: isModelUpdatePending } =
    useUpdateModel(model?.id || '')

  const { data: dataset, isLoading } = useGetDatasetDetails(
    datasetId,
    datasetId !== ''
  )

  const { data: versionsData } = useGetDatasetVersionsList(
    datasetId,
    {
      limit: MAX_ITEMS_WITHOUT_PAGINATION,
      ordering: '-created_at'
    },
    {
      enabled: datasetId !== ''
    }
  )

  const versions = versionsData?.results || []

  const activeVersion =
    versions.find((version) => version.id === datasetVersionId) || versions[0]

  const updateModelDatasetVersion = async (newVersionId: string) => {
    try {
      await updateModel({
        modelId: model?.id || '',
        data: {
          dataset_version_id: newVersionId
        }
      })
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'datasets.assign.error' })

      showToast(message, 'error')
    }
  }

  const handleVersionChange = (value: string) => {
    void updateModelDatasetVersion(value)
  }

  const handleModelTrainClick = () => {
    if (
      activeVersion?.status !== DatasetVersionStatus.Completed &&
      completionConfirmedVersionId !== activeVersion?.id
    ) {
      openConfirmCompletionModal()
      return
    }

    openTrainingDrawer()
  }

  const handleConfirmCompletion = () => {
    closeConfirmCompletionModal()
    setCompletionConfirmedVersionId(activeVersion?.id || '')
    openTrainingDrawer()
  }

  const getAndAssignLatestVersion = async (targetDatasetId: string) => {
    const data = await datasetApi.getDatasetVersionsList(targetDatasetId, {
      limit: 1,
      ordering: '-created_at'
    })

    const latestVersion = data.results[0]

    if (latestVersion) {
      void updateModelDatasetVersion(latestVersion.id)
    }
  }

  const handleDatasetVersionDeleted = () => {
    void getAndAssignLatestVersion(datasetId)
  }

  const handleDatasetCloned = (newDataset: Dataset) => {
    void getAndAssignLatestVersion(newDataset.id)
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <>
          {isLoading && <DatasetToolbarSkeleton />}

          {dataset && (
            <DatasetToolbar
              dataset={dataset}
              versions={versions}
              activeVersion={activeVersion}
              isVersionPending={isModelUpdatePending}
              onVersionChange={handleVersionChange}
              onVersionCreated={handleVersionChange}
              onVersionDeleted={handleDatasetVersionDeleted}
            />
          )}
        </>
      }
    >
      {dataset && activeVersion && (
        <Box mb="lg">
          <UploadableMediaListHandler
            key={activeVersion.id}
            version={activeVersion}
            dataset={dataset}
            showTrainModelButton
            onTrainModel={handleModelTrainClick}
            onDatasetCloned={(newDataset) =>
              void handleDatasetCloned(newDataset)
            }
          />
        </Box>
      )}

      <TrainingDrawer
        opened={isTrainingDrawerOpened}
        onClose={closeTrainingDrawer}
      >
        {application && model && dataset && (
          <CreateTrainingHandler
            applicationId={application.id}
            model={model}
            onCancel={closeTrainingDrawer}
          />
        )}
      </TrainingDrawer>

      <ConfirmDatasetCompletion
        opened={isConfirmCompletionModalOpened}
        onClose={closeConfirmCompletionModal}
        onConfirm={handleConfirmCompletion}
      />
    </PageWithFixedToolbar>
  )
}
